"use client";

import { usePathname } from "next/navigation";
import { requestOpenCookiePreferences } from "@/components/analytics/browser-consent-lifecycle";
import { isEmbedPath } from "@/lib/embed-routes";

/**
 * The site's only way to reopen consent choices once made — GDPR requires
 * withdrawal to be as easy as giving consent, but not via a permanent
 * floating widget on every page. Mounted once by the root layout (next to
 * GlobalCookieControl), in normal document flow after every page's own
 * content, so every route gets it — including standalone routes outside
 * every parenthesised layout group — without each layout needing its own
 * copy and its own chance to be forgotten.
 *
 * The generous bottom padding (well past the button itself, mobile only)
 * is not decorative: several routes render their own `fixed bottom-0`
 * mobile bar on top of normal document flow — BottomTabBar (~57px),
 * marketing/ads sticky CTAs (~73px), and the funnel sticky action
 * (~76-110px with env(safe-area-inset-bottom) on notched devices). Sitting
 * at the true end of the document, this control would otherwise scroll to
 * a position permanently covered by whichever of those bars the current
 * route renders. 8rem plus the device's own safe-area inset clears all of
 * them with margin, without this component needing to know which bar (if
 * any) a given route has.
 *
 * Not on `/embed/*`: the cookie banner is hidden there (no analytics or
 * advertising tags load in the framed widgets — lib/embed-routes.ts), so this
 * button would open nothing and its padding would eat the iframe.
 */
export function CookiePreferencesFooterLink() {
  const pathname = usePathname();
  if (isEmbedPath(pathname)) return null;
  return (
    <div className="border-t border-border px-4 pt-4 pb-[calc(8rem+env(safe-area-inset-bottom))] text-center text-xs text-muted md:pb-4">
      <button
        type="button"
        onClick={requestOpenCookiePreferences}
        className="inline-flex min-h-[44px] items-center text-sm text-muted underline hover:text-brand-700 transition-colors"
      >
        Cookie preferences
      </button>
    </div>
  );
}
